import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    CreditCard, Banknote, Check, RefreshCw,
    ArrowDownToLine, ArrowUpFromLine, Send,
} from 'lucide-react';
import BottomSheet from './BottomSheet';
import ManagerPinDialog from './ManagerPinDialog';
import PenezenkaModal from './PenezenkaModal';
import ZamkyUzaverky from './ZamkyUzaverky';
import { SheetButton, SheetTextarea, SheetAlert } from './SheetUI';
import { SHEET_TOKENS, getTextColors, getInnerCardSurface } from './sheetStyle';
import {
    Sekce, Radek, Dlazdice, VysledekVerdikt, znamenkoBarva, fmtKc, fmtKcZnamenko,
} from './UzaverkaStranka';

/**
 * Uzávěrka hlavní pokladny — **pracovní plocha fáze ukončení**, stejně jako
 * uzávěrka rozvozce (`UzaverkaStranka`).
 *
 * Byla to celostránková varianta s vlastní hlavičkou, rozbalovacím „Přepočítat
 * pokladnu" a jinak vypadajícími řádky, takže stejný úkon působil na dvou
 * místech jako dvě různé aplikace. Teď sdílí s rozvozcem rozvržení i stavební
 * díly: sekce, řádky, dvě dlaždice vedle sebe (terminál / hotovost) a stejnou
 * patičku. Sedí pod stálým pruhem cockpitu, takže osa fází zůstává na očích.
 *
 * Rozdíl proti rozvozci je v tom, co se počítá: hlavní pokladna nesčítá jen
 * tržbu, ale **všechny hotovostní pohyby** — převzaté odvody rozvozců, výplaty
 * záloh a dýšek, nákupy, vklady a výběry. Proto tu nahoře stojí jejich rozpis
 * a očekávaný stav z něj.
 *
 * **Dva kroky.** Šuplík se spočítá kdykoli, klidně současně s rozvozci. Kontrola
 * a uzavření jdou až po všech ostatních: dokud někdo stojí u pultu nebo má
 * neuzavřený flek, jeho pohyby v pokladně ještě nejsou. Napočítaná částka platí
 * pro okamžik počítání a co se stalo potom, dopočítá server
 * (`NapocitaniHlavniPokladny`), takže se znovu počítat nemusí.
 *
 * Při počítání se očekávaná částka schválně neukazuje. Pohybuje se a člověk by
 * počítal k číslu, ne peníze.
 *
 * Backend: GET pokladna-uzaverka-preview, POST pokladna-napocitat, POST pokladna-uzaverka.
 *
 * Se zapnutým provozním dnem uzávěrka zároveň uzavře den (`provozni_den`).
 */
const denPopis = (iso) => {
    if (!iso) return '';
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString('cs-CZ', { weekday: 'short', day: 'numeric', month: 'numeric' });
};

const casPopis = (iso) => (iso
    ? new Date(iso).toLocaleTimeString('cs-CZ', { hour: 'numeric', minute: '2-digit' })
    : '');

/** Jak často se při čekání na kolegy ověří, jestli už se kontrola odemkla. */
const OBNOVA_PRI_CEKANI_MS = 30000;

function Kroky({ faze, isDark }) {
    const barvy = getTextColors(isDark);
    const krok = (cislo, popis, stav) => (
        <div style={{
            flex: 1, padding: '7px 10px', borderRadius: SHEET_TOKENS.radius.input,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            fontSize: 12.5, fontWeight: 700,
            background: stav === 'hotovo' ? 'rgba(16,185,129,0.14)' : stav === 'ted' ? 'rgba(255,138,0,0.14)' : 'transparent',
            border: `1px solid ${stav === 'hotovo' ? 'rgba(16,185,129,0.32)' : stav === 'ted' ? 'rgba(255,138,0,0.34)' : barvy.divider}`,
            color: stav === 'hotovo' ? '#10B981' : stav === 'ted' ? '#FF8A00' : barvy.textMuted,
        }}>
            {stav === 'hotovo' ? <Check size={14} /> : <span>{cislo}</span>}
            {popis}
        </div>
    );
    return (
        <div style={{ display: 'flex', gap: 8 }}>
            {krok(1, faze === 'pocitani' ? 'Spočítat' : 'Spočítáno', faze === 'pocitani' ? 'ted' : 'hotovo')}
            {krok(2, 'Kontrola', faze === 'kontrola' ? 'ted' : 'pozdeji')}
        </div>
    );
}

export default function UzaverkaHlavniSheet({
    open, onClose, onSaved, smenaId,
    /* Pracovní plocha cockpitu místo překrytí sheetem. */
    inline = false,
    /* Čekající vyrovnání kolegů: když ubude, kontrola se možná odemkla. */
    pocetUkolu = 0,
    isDark, isMobile,
}) {
    const barvy = getTextColors(isDark);

    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);

    const [terminal, setTerminal] = useState('');
    const [denominace, setDenominace] = useState({});
    const [manualHotovost, setManualHotovost] = useState(null);
    const [penezenkaOtevrena, setPenezenkaOtevrena] = useState(false);
    const [ukladam, setUkladam] = useState(false);
    // Řádky „stalo se už před počítáním" — výplata se účtuje až odhlášením
    // kolegy, i když peníze dostal dřív.
    const [vyjimky, setVyjimky] = useState(() => new Set());
    const [poznamka, setPoznamka] = useState('');

    const [pinMode, setPinMode] = useState(null);     // 'confirm' | null
    const [submitError, setSubmitError] = useState(null);
    // Vědomé potvrzení nad neověřenými daty (spadlý scraper) — viz ZamkyUzaverky.
    const [prekrocitData, setPrekrocitData] = useState(false);

    // Načtení náhledu je akce, ne jednorázový efekt: zámky se dají zkontrolovat
    // znovu, aniž by se zavírala obrazovka, a po odmítnutém potvrzení
    // (data se mezitím pohnula) se čísla musí obnovit hned.
    const nactiPreview = useCallback(async ({ tiche = false } = {}) => {
        if (!tiche) {
            setFetchError(null);
            setLoading(true);
        }
        try {
            const r = await fetch(`/api/me/smena/${smenaId}/pokladna-uzaverka-preview`);
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`);
            setPreview(d);
            return d;
        } catch (e) {
            // Tichá obnova na pozadí nesmí shodit rozdělanou obrazovku.
            if (!tiche) setFetchError(e.message || 'Načtení selhalo.');
            return null;
        } finally {
            if (!tiche) setLoading(false);
        }
    }, [smenaId]);

    useEffect(() => {
        if (!open) return;
        setPreview(null); setSubmitError(null);
        setTerminal(''); setDenominace({}); setManualHotovost(null);
        setPoznamka(''); setPinMode(null); setPrekrocitData(false);
        setVyjimky(new Set());
        nactiPreview();
    }, [open, nactiPreview]);

    // Napočítaná hotovost — z počítadla nominálů nebo ručně zadané částky.
    const totalHotovost = useMemo(() => {
        if (manualHotovost != null) return Number(manualHotovost) || 0;
        let s = 0;
        for (const [nominal, count] of Object.entries(denominace)) {
            s += Number(nominal) * Number(count || 0);
        }
        return s;
    }, [denominace, manualHotovost]);

    const ocekavany = preview?.ocekavany_stav_kc || 0;
    const terminalZadan = terminal.trim() !== '' && Number.isFinite(parseFloat(terminal));
    const napocitani = preview?.napocitani ?? null;
    const hotovostZadana = !!napocitani;
    const pohybyPo = napocitani?.pohyby_po ?? [];
    // Hotovost na konci — stejný výpočet dělá při potvrzení server.
    const hotovostNaKonci = useMemo(() => {
        if (!napocitani) return 0;
        const po = pohybyPo.reduce((s, p) => (vyjimky.has(p.klic) ? s : s + p.castka_kc), 0);
        return +(napocitani.castka_kc + po).toFixed(2);
    }, [napocitani, pohybyPo, vyjimky]);
    const rozdilHotovosti = +(hotovostNaKonci - ocekavany).toFixed(2);

    // Přepočet pokladny — kartové dýško/manko (D vs G), hotovostní (E vs
    // očekávaný stav), online dýško (C). Zrcadlí HlavniFlekService::uzaverkaSmeny,
    // ať manažer potvrzuje číslo, které pak server opravdu zapíše.
    const prepocet = useMemo(() => {
        if (!preview || !hotovostZadana) return { aktivni: false };
        const E = hotovostNaKonci;
        const v = preview.vypocet || {};
        const G = v.trzba_karta_kc || 0;
        const C = v.dysko_online_kc || 0;
        const dParsed = parseFloat(terminal);
        // Bez opsaného terminálu by se kartová strana porovnala sama se sebou;
        // patička to hlídá zvlášť (`hotovo`), tady jde jen o to nespadnout.
        const D = Number.isFinite(dParsed) ? dParsed : G;
        const hDysko = Math.max(0, +(D - G).toFixed(2));
        const hManko = Math.max(0, +(G - D).toFixed(2));
        const iDysko = Math.max(0, +(E - ocekavany).toFixed(2));
        const cashManko = Math.max(0, +(ocekavany - E).toFixed(2));
        const dysko = +(iDysko + hDysko + C).toFixed(2);
        const manko = +(cashManko + hManko).toFixed(2);
        return {
            aktivni: true, D, E, C, dysko, manko,
            dyskoKarta: +(hDysko - hManko).toFixed(2),
            dyskoHotovost: +(iDysko - cashManko).toFixed(2),
            net: +(dysko - manko).toFixed(2),
        };
    }, [preview, terminal, hotovostNaKonci, hotovostZadana, ocekavany]);

    const rozvozciOtevreni = preview?.rozvozci_otevreni || 0;
    // Vyrovnaný flek ještě neznamená, že kolega odešel — může stát u pultu
    // a prodávat dál. Uzavřít den pod ním znamená, že se jeho další tržba
    // nemá kam zapsat, takže blokuje i on.
    const kolegoveNaSmene = preview?.kolegove_na_smene || [];
    // Zámky ze serveru: rozpracované objednávky (tvrdý) a stáří dat (přeskočitelný).
    const zamky = preview?.zamky || [];
    const zamekData = zamky.find(z => z.kod === 'data_ze_serveru');
    const zamkyTvrde = zamky.filter(z => !z.prekrocitelny);
    const blokovano = loading || !!fetchError || rozvozciOtevreni > 0 || kolegoveNaSmene.length > 0
        || zamkyTvrde.length > 0 || (!!zamekData && !prekrocitData);
    const hotovo = terminalZadan && hotovostZadana;
    const lzePotvrdit = !blokovano && hotovo;
    // Přeskočitelný zámek starých dat kontrole nebrání, ten se potvrzuje v ní.
    const kontrolaZamcena = !loading && !fetchError && (rozvozciOtevreni > 0 || kolegoveNaSmene.length > 0 || zamkyTvrde.length > 0);
    const faze = !napocitani ? 'pocitani' : kontrolaZamcena ? 'ceka' : 'kontrola';

    // Při čekání se kontrola odemkne sama, nikdo nemá proč klepat dokola.
    useEffect(() => {
        if (!open || faze !== 'ceka') return undefined;
        const t = setInterval(() => nactiPreview({ tiche: true }), OBNOVA_PRI_CEKANI_MS);
        return () => clearInterval(t);
    }, [open, faze, nactiPreview]);

    const predchoziUkoly = useRef(pocetUkolu);
    useEffect(() => {
        if (open && pocetUkolu < predchoziUkoly.current) nactiPreview({ tiche: true });
        predchoziUkoly.current = pocetUkolu;
    }, [open, pocetUkolu, nactiPreview]);

    const prepocitat = () => {
        setDenominace({}); setManualHotovost(null);
        setPenezenkaOtevrena(true);
    };

    const ulozNapocitani = async () => {
        if (ukladam) return;
        setUkladam(true);
        setSubmitError(null);
        const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
        try {
            const r = await fetch(`/api/me/smena/${smenaId}/pokladna-napocitat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf, Accept: 'application/json' },
                body: JSON.stringify({
                    hotovost_kc: totalHotovost,
                    hotovost_denominace: manualHotovost != null ? null : denominace,
                }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d?.error || d?.message || `HTTP ${r.status}`);
            setVyjimky(new Set());
            setPenezenkaOtevrena(false);
            await nactiPreview({ tiche: true });
        } catch (e) {
            setPenezenkaOtevrena(false);
            setSubmitError(e?.message || 'Částku se nepodařilo uložit, zkus to znovu.');
        } finally {
            setUkladam(false);
        }
    };

    const prepniVyjimku = (klic) => setVyjimky((v) => {
        const novy = new Set(v);
        if (novy.has(klic)) novy.delete(klic); else novy.add(klic);
        return novy;
    });
    // Řádky jde označit, jen když je co vysvětlovat. Když všechno sedí, nikoho
    // to nemá zdržovat otázkou.
    const lzeOznacitPohyby = pohybyPo.length > 0 && (Math.abs(rozdilHotovosti) >= 0.5 || vyjimky.size > 0);

    const handlePin = async (pin) => {
        setSubmitError(null);
        const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
        try {
            const r = await fetch(`/api/me/smena/${smenaId}/pokladna-uzaverka`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf, Accept: 'application/json' },
                body: JSON.stringify({
                    manager_pin: pin,
                    uzaverka_terminalu_kc: prepocet.D,
                    skutecna_hotovost_kc: hotovostNaKonci,
                    napocitano_kc: napocitani?.castka_kc ?? null,
                    napocitani_vyjimky: pohybyPo.filter(p => vyjimky.has(p.klic)).flatMap(p => p.ids),
                    poznamka: poznamka.trim() || null,
                    // Čísla z obrazovky — server je porovná s aktuálními a při
                    // rozdílu uzávěrku odmítne (409). Manažer tak nepotvrdí
                    // jiný rozdíl, než jaký viděl. Posílají se jen ta tři,
                    // která uzávěrka hlavní pokladny opravdu ukazuje.
                    otisk: preview?.otisk ? {
                        ocekavany_stav_kc: preview.otisk.ocekavany_stav_kc,
                        trzba_karta_kc:    preview.otisk.trzba_karta_kc,
                        dysko_online_kc:   preview.otisk.dysko_online_kc,
                    } : null,
                    prekrocit_stara_data: prekrocitData,
                }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                // Data se mezi náhledem a potvrzením pohnula (nebo padl zámek):
                // klávesnice PINu je na vysvětlování malá — zavřít, načíst
                // čerstvá čísla a říct to v ploše uzávěrky.
                if (r.status === 409 || data?.zamky) {
                    setPinMode(null);
                    setSubmitError(data?.error || 'Data se změnila — zkontroluj čísla znovu.');
                    await nactiPreview();
                    return { ok: true };   // obslouženo tady, dialog už nemá co hlásit
                }
                return { ok: false, status: r.status, error: data?.error || `HTTP ${r.status}` };
            }
            setPinMode(null);
            onSaved?.();
            return { ok: true };
        } catch (e) {
            return { ok: false, error: e?.message || 'Síťová chyba' };
        }
    };

    // ─── Render ──────────────────────────────────────────────────────────────

    const obsah = (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{
                flex: 1, overflowY: 'auto', overscrollBehavior: 'contain',
                padding: `4px ${SHEET_TOKENS.spacing.sheetPadX}px 12px`,
                display: 'flex', flexDirection: 'column', gap: 16,
            }}>
                {loading && (
                    <div style={{ padding: 24, textAlign: 'center', color: barvy.textMuted, fontSize: 13 }}>
                        Načítám…
                    </div>
                )}

                {fetchError && <SheetAlert kind="error" isDark={isDark}>{fetchError}</SheetAlert>}

                {!loading && !fetchError && preview && (
                    <>
                        <Kroky faze={faze} isDark={isDark} />

                        {faze === 'pocitani' && (
                            <div style={{
                                ...getInnerCardSurface(isDark),
                                borderRadius: SHEET_TOKENS.radius.card, padding: '12px 14px',
                                display: 'flex', flexDirection: 'column', gap: 4,
                            }}>
                                <span style={{ fontSize: 15, fontWeight: 800, color: barvy.textPrimary }}>Spočítej šuplík</span>
                                <span style={{ fontSize: 13, lineHeight: 1.45, color: barvy.textSecondary }}>
                                    Můžeš hned, na nikoho nečekáš. Peníze od kolegů si zatím dej stranou,
                                    co přijde po spočítání, systém započte sám.
                                </span>
                            </div>
                        )}

                        {faze === 'ceka' && (
                            <>
                                <div style={{
                                    ...getInnerCardSurface(isDark),
                                    borderRadius: SHEET_TOKENS.radius.card, padding: '12px 14px',
                                    display: 'flex', alignItems: 'center', gap: 12,
                                }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: barvy.textMuted }}>
                                            Napočítáno v {casPopis(napocitani.v)}
                                        </div>
                                        <div style={{
                                            fontSize: 24, fontWeight: 800, letterSpacing: -0.4, color: barvy.textPrimary,
                                            fontVariantNumeric: 'tabular-nums',
                                        }}>{fmtKc(napocitani.castka_kc)}</div>
                                    </div>
                                    <SheetButton isDark={isDark} variant="secondary" icon={<RefreshCw size={15} />}
                                        onClick={prepocitat} style={{ flex: '0 0 auto', width: 'auto', padding: '0 14px', fontSize: 13, whiteSpace: 'nowrap' }}>
                                        Přepočítat
                                    </SheetButton>
                                </div>
                                {Math.abs(napocitani.predbezny_rozdil_kc) < 0.5 ? (
                                    <SheetAlert kind="success" isDark={isDark}>
                                        Předběžně to sedí. Finální výsledek bude v kroku 2.
                                    </SheetAlert>
                                ) : (
                                    <SheetAlert kind="warn" isDark={isDark}>
                                        Předběžně {napocitani.predbezny_rozdil_kc < 0 ? 'chybí' : 'přebývá'}{' '}
                                        <strong>{fmtKc(Math.abs(napocitani.predbezny_rozdil_kc))}</strong>.
                                        {' '}Pokud jsi už někomu vyplatil, doladí se to v kroku 2.
                                    </SheetAlert>
                                )}
                            </>
                        )}

                        {/* Překážky napřed — ať je vidět, proč je patička zamčená. */}
                        {/* Kolegy na směně ukazuje zámek „kolegove" i s důvodem — tady
                            jen záloha pro případ, že by ho server neposlal. */}
                        {kolegoveNaSmene.length > 0 && !zamky.some(z => z.kod === 'kolegove') && (
                            <SheetAlert kind="warn" isDark={isDark}>
                                Na směně {kolegoveNaSmene.length === 1 ? 'je ještě' : 'jsou ještě'}{' '}
                                <strong>{kolegoveNaSmene.join(', ')}</strong> — uzávěrka je až po nich.
                            </SheetAlert>
                        )}
                        {rozvozciOtevreni > 0 && (
                            <SheetAlert kind="warn" isDark={isDark}>
                                Nejdřív musí uzavřít všichni rozvozci — zbývá <strong>{rozvozciOtevreni}</strong>{' '}
                                neuzavřených fleků. Jejich hotovost padá sem, takže dřív pokladnu spočítat nejde.
                            </SheetAlert>
                        )}

                        <ZamkyUzaverky
                            zamky={zamky}
                            neukoncene={preview.neukoncene}
                            kolegove={preview.kolegove || []}
                            dataServer={preview.data_ze_serveru}
                            cekaNaPlatbu={preview.ceka_na_platbu}
                            prekrocit={prekrocitData}
                            onPrekrocit={setPrekrocitData}
                            onZnovu={nactiPreview}
                            nacitam={loading}
                            isDark={isDark}
                        />

                        {faze === 'kontrola' && (<>
                        <Sekce nadpis="Přišlo do pokladny" isDark={isDark} barva="#10B981" Ikona={ArrowDownToLine}>
                            {(preview.prijmy || []).length === 0
                                ? <Radek popis="Žádné položky" hodnota="—" tlumene isDark={isDark} />
                                : preview.prijmy.map((p, i) => (
                                    <Radek key={i} popis={p.label} detail={p.detail}
                                        hodnota={fmtKc(p.castka)} isDark={isDark} />
                                ))}
                        </Sekce>

                        <Sekce nadpis="Odešlo z pokladny" isDark={isDark} barva="#EF4444" Ikona={ArrowUpFromLine}>
                            {(preview.vydaje || []).length === 0
                                ? <Radek popis="Žádné položky" hodnota="—" tlumene isDark={isDark} />
                                : preview.vydaje.map((p, i) => (
                                    <Radek key={i} popis={p.label} detail={p.detail}
                                        hodnota={fmtKc(p.castka)} isDark={isDark} />
                                ))}
                        </Sekce>

                        <Sekce nadpis="Co říká systém" isDark={isDark}>
                            <Radek popis="Tržba karta" hodnota={fmtKc(preview.vypocet?.trzba_karta_kc)} isDark={isDark} />
                            <Radek popis="Online dýško" hodnota={fmtKc(preview.vypocet?.dysko_online_kc)} isDark={isDark} />
                            <Radek popis="Očekávaná hotovost v pokladně" hodnota={fmtKc(ocekavany)} velky isDark={isDark} />
                        </Sekce>

                        <Sekce nadpis="Hotovost v šuplíku" isDark={isDark} barva="#FF8A00" Ikona={Banknote}>
                            <Radek popis={`Napočítáno v ${casPopis(napocitani.v)}`} hodnota={fmtKc(napocitani.castka_kc)} isDark={isDark} />
                            {lzeOznacitPohyby && (
                                <span style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.4, color: '#FF8A00', padding: '4px 0' }}>
                                    Stalo se něco z tohoto už před počítáním? Klepni na to.
                                </span>
                            )}
                            {pohybyPo.map((p) => {
                                const oznacen = vyjimky.has(p.klic);
                                if (!lzeOznacitPohyby) {
                                    return <Radek key={p.klic} popis={p.popis} detail={casPopis(p.cas)}
                                        hodnota={fmtKcZnamenko(p.castka_kc)} barva={znamenkoBarva(p.castka_kc)} isDark={isDark} />;
                                }
                                return (
                                    <button key={p.klic} type="button" onClick={() => prepniVyjimku(p.klic)}
                                        aria-pressed={oznacen}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                                            minHeight: 44, padding: '6px 10px', textAlign: 'left', cursor: 'pointer',
                                            borderRadius: SHEET_TOKENS.radius.input,
                                            background: oznacen ? 'rgba(16,185,129,0.14)' : 'transparent',
                                            border: `1px solid ${oznacen ? '#10B981' : barvy.divider}`,
                                            color: barvy.textPrimary, fontSize: 13.5,
                                        }}>
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            {p.popis}
                                            <span style={{ display: 'block', fontSize: 11.5, color: barvy.textMuted }}>
                                                {oznacen ? 'Už v napočítané částce' : casPopis(p.cas)}
                                            </span>
                                        </span>
                                        <span style={{
                                            fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                                            color: oznacen ? barvy.textMuted : znamenkoBarva(p.castka_kc),
                                            textDecoration: oznacen ? 'line-through' : 'none',
                                        }}>{fmtKcZnamenko(p.castka_kc)}</span>
                                    </button>
                                );
                            })}
                            <Radek popis="Hotovost na konci" hodnota={fmtKc(hotovostNaKonci)} velky isDark={isDark} />
                            {Math.abs(rozdilHotovosti) >= 0.5 && (
                                <Radek popis="Proti očekávané hotovosti" hodnota={fmtKcZnamenko(rozdilHotovosti)}
                                    barva={znamenkoBarva(rozdilHotovosti)} isDark={isDark} />
                            )}
                            <div style={{ paddingTop: 4 }}>
                                <SheetButton isDark={isDark} variant="secondary" icon={<RefreshCw size={15} />}
                                    onClick={prepocitat} style={{ flex: '0 0 auto', width: 'auto', padding: '0 14px', fontSize: 13, whiteSpace: 'nowrap' }}>
                                    Přepočítat šuplík
                                </SheetButton>
                            </div>
                        </Sekce>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Dlazdice
                                    Ikona={CreditCard} label="Terminál" akcent="#3B82F6"
                                    vyplneno={terminalZadan} isDark={isDark}
                                >
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            value={terminal}
                                            onChange={e => setTerminal(e.target.value.replace(/[^0-9.,]/g, ''))}
                                            placeholder="—"
                                            aria-label="Částka z uzávěrky terminálu"
                                            style={{
                                                flex: 1, minWidth: 0, background: 'transparent',
                                                border: 'none', outline: 'none', color: barvy.textPrimary,
                                                fontSize: 20, fontWeight: 800, letterSpacing: -0.4,
                                                fontVariantNumeric: 'tabular-nums', padding: 0,
                                            }}
                                        />
                                        <span style={{ fontSize: 12, fontWeight: 700, color: barvy.textMuted, flexShrink: 0 }}>Kč</span>
                                    </div>
                                </Dlazdice>
                            </div>

                            <span style={{
                                fontSize: 12.5, lineHeight: 1.4,
                                color: hotovo ? barvy.textMuted : '#FF8A00',
                            }}>
                                {terminalZadan ? 'Zkontroluj výsledek.' : 'Zbývá opsat číslo z uzávěrky terminálu.'}
                            </span>
                        </div>

                        <VysledekVerdikt
                            hotovo={hotovo}
                            net={prepocet.net}
                            poznamkaManka="Zapíše se jako manko pokladny."
                            vetaCekani="Opiš terminál a uvidíš výsledek."
                            radky={[
                                { popis: 'Karta', hodnota: terminalZadan ? fmtKcZnamenko(prepocet.dyskoKarta) : 'čeká na terminál',
                                  tlumene: !terminalZadan, barva: terminalZadan ? znamenkoBarva(prepocet.dyskoKarta) : null },
                                { popis: 'Hotovost', hodnota: hotovostZadana ? fmtKcZnamenko(prepocet.dyskoHotovost) : 'čeká na součet',
                                  tlumene: !hotovostZadana, barva: hotovostZadana ? znamenkoBarva(prepocet.dyskoHotovost) : null },
                                { popis: 'Online dýško', hodnota: fmtKc(prepocet.C) },
                            ]}
                            isDark={isDark}
                        />

                        <SheetTextarea
                            isDark={isDark}
                            label="Poznámka (volitelná)"
                            placeholder="Cokoli, co má k uzávěrce zůstat zapsané…"
                            value={poznamka}
                            onChange={e => setPoznamka(e.target.value)}
                        />

                        {preview.provozni_den && (
                            <SheetAlert kind="success" isDark={isDark}>
                                Uzávěrkou skončí provozní den
                                {preview.provozni_den.datum ? <> <strong>{denPopis(preview.provozni_den.datum)}</strong></> : null}.
                                Pokladny se zamknou a objednávky, které přijdou potom, spadnou
                                do dne <strong>{denPopis(preview.provozni_den.dalsi)}</strong>.
                            </SheetAlert>
                        )}
                        </>)}

                        {submitError && <SheetAlert kind="error" isDark={isDark}>{submitError}</SheetAlert>}
                    </>
                )}
            </div>

            <div style={{
                flexShrink: 0,
                padding: `0 ${SHEET_TOKENS.spacing.sheetPadX}px`,
                display: 'flex', gap: 8,
            }}>
                {faze === 'pocitani' && !loading && !fetchError ? (
                    <SheetButton
                        isDark={isDark}
                        variant="primary"
                        accent="#FF8A00"
                        accent2="#FFA733"
                        icon={<Banknote size={16} />}
                        onClick={() => setPenezenkaOtevrena(true)}
                        style={{ flex: 1 }}
                    >
                        Spočítat šuplík
                    </SheetButton>
                ) : (
                <SheetButton
                    isDark={isDark}
                    variant="primary"
                    accent={hotovo && prepocet.net < 0 ? '#EF4444' : '#1D9E75'}
                    accent2={hotovo && prepocet.net < 0 ? '#F87171' : '#10B981'}
                    icon={<Send size={16} />}
                    disabled={!lzePotvrdit}
                    onClick={() => setPinMode('confirm')}
                    style={{ flex: 1 }}
                >
                    {faze === 'ceka'
                        ? 'Kontrola až po kolezích'
                        : preview?.provozni_den ? 'Uzavřít pokladnu a den' : 'Uzavřít pokladnu'}
                </SheetButton>
                )}
            </div>

            {penezenkaOtevrena && (
                <PenezenkaModal
                    zaklad={0}
                    nadpis="Hlavní pokladna"
                    popisZakladu="Spočítej, co je teď v šuplíku"
                    radekText="Peníze od kolegů si zatím dej stranou"
                    denom={denominace}
                    manualE={manualHotovost}
                    onZmena={(zmena) => {
                        if ('denom' in zmena) setDenominace(zmena.denom);
                        if ('manualE' in zmena) setManualHotovost(zmena.manualE);
                    }}
                    onHotovo={ulozNapocitani}
                    onClose={() => setPenezenkaOtevrena(false)}
                    isDark={isDark}
                    isMobile={isMobile}
                />
            )}

            {pinMode && (
                <ManagerPinDialog
                    title="PIN manažera směny"
                    subtitle="Potvrzení uzávěrky"
                    onClose={() => setPinMode(null)}
                    onSubmit={handlePin}
                    isDark={isDark}
                />
            )}
        </div>
    );

    // Inline = plocha modulu: žádný povrch, žádné překrytí (norma rozvržení).
    if (inline) return obsah;

    return (
        <BottomSheet open={open} onClose={onClose} isDark={isDark} isMobile={isMobile}
            fullscreen title={null} hideClose>
            {obsah}
        </BottomSheet>
    );
}
